import type {
  SiteBuilderBlock,
  SiteBuilderBlockDefinition,
  SiteBuilderDropLine,
  SiteBuilderZoneKind,
} from "@webdo/site-templates/builder";
import type {
  AdvancedCanvasRect,
  AdvancedCanvasResizeHandle,
  AdvancedCanvasSlotOverlay,
} from "./advanced";
import type {
  BuilderStudioGridPlacement,
} from "./advancedOrchestration";

export type BuilderStudioGridMetrics = AdvancedCanvasRect & {
  cellHeight: number;
  cellWidth: number;
  columnGap: number;
  columns: number;
  rowGap: number;
  rows: number;
};

export type BuilderStudioMeasuredChild = {
  index: number;
  rect: DOMRect;
};

export type BuilderStudioGridAvailabilityInput = {
  blocks: readonly SiteBuilderBlock[];
  columns: number;
  ignoredIndex?: number | null;
  placement: BuilderStudioGridPlacement;
};

export type BuilderStudioGridBoundsInput = Pick<
  BuilderStudioGridAvailabilityInput,
  "columns" | "placement"
>;

export type BuilderStudioAutomaticGridPlacementInput = {
  anchor?: SiteBuilderBlock | null;
  blocks: readonly SiteBuilderBlock[];
  columns: number;
  footprint: Pick<BuilderStudioGridPlacement, "h" | "w">;
};

export type BuilderStudioResponsiveGridReflowInput = {
  blocks: readonly SiteBuilderBlock[];
  columns: number;
  ignoredIndex: number;
  visualInsertIndex: number;
};

export type BuilderStudioMaterializedGridLineDrop = {
  block: SiteBuilderBlock;
  placement: BuilderStudioGridPlacement;
};

export type BuilderStudioInlineMinHeightSnapshot = {
  priority: string;
  value: string;
};

export const BUILDER_STUDIO_EXTENDED_GRID_MAX_EXTRA_ROWS = 12;

export type BuilderStudioPointerDragActivation = "any" | "horizontal";

export type BuilderStudioSlotPointerHit = {
  directHit: boolean;
  matches: boolean;
};

/**
 * Keeps the visible slot guide tied to its content. Presentation containers
 * may intentionally be very tall (hero media, maps, contact panels), but that
 * extra height should enlarge only the hit target, not stretch the guide.
 */
export function builderStudioSlotOverlayRect({
  containerRect,
  contentRects,
}: {
  containerRect: AdvancedCanvasRect;
  contentRects: readonly AdvancedCanvasRect[];
}): AdvancedCanvasRect {
  const containerBottom = containerRect.top + containerRect.height;
  const contentTop = contentRects.length > 0
    ? Math.max(
        containerRect.top,
        Math.min(...contentRects.map((rect) => rect.top)),
      )
    : null;
  const contentBottom = contentRects.length > 0
    ? Math.min(
        containerBottom,
        Math.max(...contentRects.map((rect) => rect.top + rect.height)),
      )
    : null;

  if (
    contentTop !== null
    && contentBottom !== null
    && contentBottom > contentTop
  ) {
    const contentHeight = contentBottom - contentTop;
    const height = Math.min(96, contentHeight);
    const verticalInset = Math.min(
      12,
      Math.max(0, (contentHeight - height) / 2),
    );

    return {
      height,
      left: containerRect.left,
      top: contentTop + verticalInset,
      width: containerRect.width,
    };
  }

  const horizontalInset = Math.min(
    12,
    Math.max(0, containerRect.width / 4),
  );
  const height = Math.max(1, Math.min(96, containerRect.height));
  const verticalInset = Math.min(
    12,
    Math.max(0, (containerRect.height - height) / 2),
  );

  return {
    height,
    left: containerRect.left + horizontalInset,
    top: containerRect.top + verticalInset,
    width: Math.max(1, containerRect.width - (horizontalInset * 2)),
  };
}

export type BuilderStudioGridAnchorCandidate = {
  index: number;
  paintOrder?: number;
  rect: AdvancedCanvasRect;
  zIndex?: number;
};

export function builderStudioInlineMinHeightSnapshot(
  element: Pick<HTMLElement, "style">,
): BuilderStudioInlineMinHeightSnapshot {
  return {
    priority: element.style.getPropertyPriority("min-height"),
    value: element.style.getPropertyValue("min-height"),
  };
}

export function builderStudioSetExtendedGridMinHeight(
  element: Pick<HTMLElement, "style">,
  value: string,
): void {
  // Template renderers may intentionally reset their responsive min-height
  // with `!important` (Morrow does this for both the region and its grid).
  // The temporary drag expansion must win that cascade without editing the
  // authored template styles.
  element.style.setProperty("min-height", value, "important");
}

export function builderStudioRestoreInlineMinHeight(
  element: Pick<HTMLElement, "style">,
  snapshot: BuilderStudioInlineMinHeightSnapshot,
): void {
  if (snapshot.value === "") {
    element.style.removeProperty("min-height");
    return;
  }

  element.style.setProperty(
    "min-height",
    snapshot.value,
    snapshot.priority,
  );
}

export function builderStudioPointerDragActivationForPointer(
  pointerType: string,
  touchActivation: BuilderStudioPointerDragActivation,
): BuilderStudioPointerDragActivation {
  return pointerType === "touch" ? touchActivation : "any";
}

export function builderStudioPointerDragDecision({
  activation,
  deltaX,
  deltaY,
  threshold = 8,
}: {
  activation: BuilderStudioPointerDragActivation;
  deltaX: number;
  deltaY: number;
  threshold?: number;
}): "activate" | "cancel" | "pending" {
  if (Math.hypot(deltaX, deltaY) < threshold) {
    return "pending";
  }

  if (
    activation === "horizontal"
    && Math.abs(deltaY) > Math.abs(deltaX)
  ) {
    return "cancel";
  }

  return "activate";
}

/**
 * Resolves the rendered grid child under the pointer while excluding the
 * block that is currently being moved. The source stays rendered during a
 * pointer drag, so failing to exclude it makes overlapping blocks target
 * themselves instead of the sibling underneath.
 */
export function builderStudioGridAnchorAtPoint<
  Candidate extends BuilderStudioGridAnchorCandidate,
>({
  candidates,
  clientX,
  clientY,
  ignoredIndex = null,
}: {
  candidates: readonly Candidate[];
  clientX: number;
  clientY: number;
  ignoredIndex?: number | null;
}): Candidate | null {
  return candidates
    .map((candidate, documentOrder) => ({ candidate, documentOrder }))
    .filter(({ candidate }) => (
      candidate.index !== ignoredIndex
      && clientX >= candidate.rect.left
      && clientX <= candidate.rect.left + candidate.rect.width
      && clientY >= candidate.rect.top
      && clientY <= candidate.rect.top + candidate.rect.height
    ))
    .sort((left, right) => {
      const leftPaintOrder = Number.isFinite(left.candidate.paintOrder)
        && Number(left.candidate.paintOrder) >= 0
        ? Number(left.candidate.paintOrder)
        : Number.POSITIVE_INFINITY;
      const rightPaintOrder = Number.isFinite(right.candidate.paintOrder)
        && Number(right.candidate.paintOrder) >= 0
        ? Number(right.candidate.paintOrder)
        : Number.POSITIVE_INFINITY;

      if (leftPaintOrder !== rightPaintOrder) {
        return leftPaintOrder - rightPaintOrder;
      }

      const leftZIndex = Number.isFinite(left.candidate.zIndex)
        ? Number(left.candidate.zIndex)
        : 0;
      const rightZIndex = Number.isFinite(right.candidate.zIndex)
        ? Number(right.candidate.zIndex)
        : 0;

      return rightZIndex - leftZIndex
        || right.documentOrder - left.documentOrder;
    })[0]?.candidate ?? null;
}

export function builderStudioExtendedGridExtraRowLimit({
  blocked,
  extendedRows,
}: {
  blocked: boolean;
  extendedRows: boolean;
}): number {
  return extendedRows && !blocked
    ? BUILDER_STUDIO_EXTENDED_GRID_MAX_EXTRA_ROWS
    : 0;
}

export function builderStudioPreferredExtendedGridTarget<
  Target extends {
    area?: number;
    blocked: boolean;
    directHit?: boolean;
    emptyOverlay?: boolean;
    eventTargetDistance?: number;
    eventTargetHit?: boolean;
    key: string;
    lineInsertion?: boolean;
    parentKey?: string | null;
    syntheticOverlay?: boolean;
    zoneKind?: SiteBuilderZoneKind | null;
  },
>(
  targets: readonly Target[],
  activeKey: string | null | undefined,
  gridPreference: boolean | "unless-direct-nested" = false,
): Target | null {
  const preferGrid = gridPreference !== false;
  const allowDirectNestedTarget = gridPreference === "unless-direct-nested";
  const unblockedGridTargets = targets.filter(
    (target) => target.zoneKind === "grid" && !target.blocked,
  );
  const directNestedTargets = targets.filter((target) => (
    !target.blocked
    && target.directHit === true
    && (
      (
        target.lineInsertion === true
        && target.eventTargetHit === true
      )
      || (
        target.zoneKind === "slot"
        && (
          target.eventTargetHit === true
          || target.emptyOverlay === true
        )
      )
    )
  ));
  const eligibleTargets = preferGrid && unblockedGridTargets.length > 0
    ? allowDirectNestedTarget && directNestedTargets.length > 0
      ? targets.filter((target) => (
          (target.zoneKind === "grid" && !target.blocked)
          || directNestedTargets.includes(target)
        ))
      : unblockedGridTargets
    : targets;
  const active = (
    activeKey
      ? eligibleTargets.find(
          (target) => target.key === activeKey && !target.blocked,
        )
      : null
  ) ?? null;
  const eventTargetCandidates = eligibleTargets.filter(
    (target) => target.eventTargetHit === true,
  );
  const unblockedEventTargetCandidates = eventTargetCandidates.filter(
    (target) => !target.blocked,
  );
  const eventTarget = (
    unblockedEventTargetCandidates.length > 0
      ? unblockedEventTargetCandidates
      : eventTargetCandidates
  ).reduce<Target | null>((closest, target) => {
    if (!closest) {
      return target;
    }

    const closestDistance = closest.eventTargetDistance
      ?? Number.POSITIVE_INFINITY;
    const targetDistance = target.eventTargetDistance
      ?? Number.POSITIVE_INFINITY;

    if (targetDistance !== closestDistance) {
      return targetDistance < closestDistance ? target : closest;
    }

    return (target.area ?? Number.POSITIVE_INFINITY)
      < (closest.area ?? Number.POSITIVE_INFINITY)
      ? target
      : closest;
  }, null);
  const direct = eligibleTargets.find((target) => (
    target.directHit === true && !target.blocked
  )) ?? eligibleTargets.find((target) => target.directHit === true) ?? null;
  const directEmptyOverlay = eligibleTargets.find((target) => (
    target.emptyOverlay === true
    && target.directHit === true
    && !target.blocked
  )) ?? null;
  const explicitSiblingEmptyOverlay = (
    directEmptyOverlay?.syntheticOverlay === true
      ? eligibleTargets.find((target) => (
          target.emptyOverlay === true
          && target.syntheticOverlay !== true
          && target.zoneKind === "grid"
          && !target.blocked
          && target.parentKey === directEmptyOverlay.parentKey
        ))
      : null
  ) ?? null;

  if (explicitSiblingEmptyOverlay) {
    return explicitSiblingEmptyOverlay;
  }

  if (
    directEmptyOverlay
    && (
      directEmptyOverlay.syntheticOverlay !== true
      || !eventTarget
      || eventTarget.blocked
      || directEmptyOverlay.parentKey !== eventTarget.parentKey
    )
  ) {
    return directEmptyOverlay;
  }

  if (
    active?.emptyOverlay === true
    && active.zoneKind === "grid"
    && direct
    && direct.key !== active.key
    && direct.parentKey === active.parentKey
  ) {
    return active;
  }

  if (eventTarget) {
    return eventTarget;
  }

  if (direct) {
    return direct;
  }

  return active ?? eligibleTargets[0] ?? null;
}

/**
 * Resolves the geometric part of slot targeting. An extended grid may grow
 * below its authored bounds while the pointer is stationary. When the canvas
 * is auto-scrolling down, treating that newly grown area as a direct hit keeps
 * the same grid under the pointer and continuously pushes the next section
 * away. Clamp only the active grid back to its authored height in that case;
 * direct neighbouring slots can then take over on the next measurement.
 */
export function builderStudioSlotPointerHit({
  activeExtendedKey,
  blocked,
  clientX,
  clientY,
  extendedHitRowStep,
  extendedRows,
  originalHeight,
  rect,
  scrollingDown,
  targetKey,
}: {
  activeExtendedKey?: string | null;
  blocked: boolean;
  clientX: number;
  clientY: number;
  extendedHitRowStep: number;
  extendedRows: boolean;
  originalHeight?: number | null;
  rect: AdvancedCanvasRect;
  scrollingDown: boolean;
  targetKey: string;
}): BuilderStudioSlotPointerHit {
  const releaseActiveExtension = scrollingDown
    && activeExtendedKey === targetKey
    && originalHeight !== null
    && originalHeight !== undefined;
  const hitHeight = releaseActiveExtension
    ? Math.min(rect.height, Math.max(1, originalHeight))
    : rect.height;
  const right = rect.left + rect.width;
  const bottom = rect.top + hitHeight;
  const horizontalHit = clientX >= rect.left && clientX <= right;
  const directHit = horizontalHit
    && clientY >= rect.top
    && clientY <= bottom;
  const extendedHit = !releaseActiveExtension
    && extendedRows
    && !blocked
    && horizontalHit
    && clientY >= rect.top
    && clientY <= bottom + (
      BUILDER_STUDIO_EXTENDED_GRID_MAX_EXTRA_ROWS
      * Math.max(1, extendedHitRowStep)
    );

  return {
    directHit,
    matches: directHit || extendedHit,
  };
}

export function builderStudioDropLinesForZone(
  dropLines: readonly SiteBuilderDropLine[],
  zoneKind: SiteBuilderZoneKind | null | undefined,
): SiteBuilderDropLine[] {
  if (zoneKind !== "textFlow") {
    return [...dropLines];
  }

  return [
    "before",
    "after",
    ...dropLines.filter(
      (line) => line !== "before" && line !== "after",
    ),
  ];
}

const buttonLikeBlockTypes = new Set([
  "addToCartButton",
  "appointmentButton",
  "badge",
  "button",
  "ctaBadge",
  "donationButton",
  "facebook",
  "headerCartButton",
  "headerFavoritesButton",
  "instagram",
  "link",
  "socialLink",
  "supportedPaymentButton",
]);

function finiteNumber(
  value: unknown,
  fallback: number,
): number {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : fallback;
}

function positiveInteger(
  value: unknown,
  fallback: number,
): number {
  return Math.max(1, Math.round(finiteNumber(value, fallback)));
}

function cssPixelValue(
  value: unknown,
  fallback = 0,
): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : fallback;
  }

  if (typeof value !== "string") {
    return fallback;
  }

  const parsed = Number.parseFloat(value.trim());

  return Number.isFinite(parsed) ? parsed : fallback;
}

function numericDataAttribute(
  element: HTMLElement,
  name: string,
): number | null {
  const value = element.getAttribute(name);
  const parsed = Number(value);

  return Number.isFinite(parsed) && parsed > 0
    ? Math.max(1, Math.round(parsed))
    : null;
}

function computedGridTrackCount(value: string): number | null {
  const normalized = value.trim();

  if (!normalized || normalized === "none") {
    return null;
  }

  const repeat = normalized.match(/^repeat\(\s*(\d+)\s*,/i);

  if (repeat?.[1]) {
    return positiveInteger(repeat[1], 1);
  }

  const tracks = normalized
    .replace(/\([^)]*\)/g, (match) => match.replace(/\s+/g, "\u00a0"))
    .split(/\s+/)
    .filter(Boolean);

  return tracks.length > 0 ? tracks.length : null;
}

function computedGridTrackPixelSize(value: string): number | null {
  const normalized = value.trim();

  if (!normalized || normalized === "none") {
    return null;
  }

  const repeatedTrack = normalized.match(
    /^repeat\(\s*\d+\s*,\s*(\d+(?:\.\d+)?)px\s*\)$/i,
  );

  if (repeatedTrack?.[1]) {
    return Number(repeatedTrack[1]);
  }

  const tracks = normalized
    .replace(/\([^)]*\)/g, (match) => match.replace(/\s+/g, "\u00a0"))
    .split(/\s+/)
    .filter(Boolean);
  const pixelTracks = tracks.flatMap((track) => {
    const match = track.match(/^(\d+(?:\.\d+)?)px$/i);

    return match?.[1] ? [Number(match[1])] : [];
  });

  if (
    pixelTracks.length === 0
    || pixelTracks.length !== tracks.length
  ) {
    return null;
  }

  return pixelTracks.reduce((sum, track) => sum + track, 0)
    / pixelTracks.length;
}

export function builderStudioGridElementUsesDeclaredTracks(
  element: HTMLElement,
): boolean {
  const computed = element.ownerDocument.defaultView?.getComputedStyle(element)
    ?? globalThis.getComputedStyle(element);

  if (computed.display !== "grid" && computed.display !== "inline-grid") {
    return false;
  }

  const declaredColumns = numericDataAttribute(
    element,
    "data-builder-grid-cols",
  );
  const renderedColumns = computedGridTrackCount(
    computed.gridTemplateColumns,
  );

  return declaredColumns === null
    || renderedColumns === null
    || renderedColumns >= declaredColumns;
}

function childGridExtent(blocks: readonly SiteBuilderBlock[]): {
  columns: number;
  rows: number;
} {
  return blocks.reduce((extent, block) => {
    const x = positiveInteger(block.options?.gridX, 1);
    const y = positiveInteger(block.options?.gridY, 1);
    const w = positiveInteger(block.options?.gridW, 1);
    const h = positiveInteger(block.options?.gridH, 1);

    return {
      columns: Math.max(extent.columns, x + w - 1),
      rows: Math.max(extent.rows, y + h - 1),
    };
  }, {
    columns: 1,
    rows: 1,
  });
}

/**
 * Reads the rendered grid instead of assuming fixed desktop dimensions.
 * This keeps drag guides aligned in the desktop canvas and both iframe
 * preview sizes, matching the original Shop editor behaviour.
 */
export function builderStudioGridMetricsForElement({
  blocks,
  element,
  parentBlock,
}: {
  blocks: readonly SiteBuilderBlock[];
  element: HTMLElement;
  parentBlock: SiteBuilderBlock;
}): BuilderStudioGridMetrics {
  const rect = element.getBoundingClientRect();
  const computed = element.ownerDocument.defaultView?.getComputedStyle(element)
    ?? globalThis.getComputedStyle(element);
  const renderedAsDeclaredGrid = builderStudioGridElementUsesDeclaredTracks(
    element,
  );
  const emptyOverlay = element.getAttribute("data-builder-empty-slot") === "true";
  const renderedColumnGap = cssPixelValue(computed.columnGap, 0);
  const rowGap = cssPixelValue(computed.rowGap, 0);
  const extent = childGridExtent(blocks);
  const computedColumns = computedGridTrackCount(
    computed.gridTemplateColumns,
  );
  const computedRows = computedGridTrackCount(computed.gridTemplateRows);
  const renderedRowHeight = computedGridTrackPixelSize(
    computed.gridTemplateRows,
  );
  const columns = Math.max(
    extent.columns,
    numericDataAttribute(element, "data-builder-grid-cols")
      ?? computedColumns
    ?? positiveInteger(parentBlock.options?.gridColumns, 12),
  );
  const columnGap = renderedAsDeclaredGrid
    ? renderedColumnGap
    : Math.min(
        renderedColumnGap,
        rect.width / Math.max(1, columns * 4),
      );
  const configuredRowHeight = cssPixelValue(
    parentBlock.options?.gridRowHeight,
    0,
  );
  const authoredRowHeight = configuredRowHeight > 0
    ? configuredRowHeight
    : 48;
  // CSS `minmax(48px, auto)` lets tall images, maps and forms enlarge their
  // rendered tracks. That presentation height must never redefine the shared
  // editor grid: even a correctly authored multi-row block would otherwise
  // turn every one of its logical slots into a 100px+ row.
  const stretchedRenderedRows = renderedRowHeight !== null
    && renderedRowHeight > authoredRowHeight * 1.5;
  const rowHeightHint = (
    renderedAsDeclaredGrid && !stretchedRenderedRows
      ? renderedRowHeight
      : null
  )
    ?? authoredRowHeight;
  const compactRowsForRenderedHeight = Math.max(
    1,
    Math.ceil(
      Math.max(1, rect.height)
      / (Math.max(1, rowHeightHint) + rowGap),
    ),
  );
  const rows = Math.max(
    extent.rows,
    numericDataAttribute(element, "data-builder-grid-rows")
      ?? computedRows
      ?? 1,
    renderedAsDeclaredGrid && !emptyOverlay && !stretchedRenderedRows
      ? 1
      : compactRowsForRenderedHeight,
  );
  const cellWidth = Math.max(
    1,
    (rect.width - (Math.max(0, columns - 1) * columnGap)) / columns,
  );
  const measuredRowHeight = (
    rect.height - (Math.max(0, rows - 1) * rowGap)
  ) / rows;
  const fallbackCellHeight = stretchedRenderedRows
    ? authoredRowHeight
    : configuredRowHeight > 0
      ? configuredRowHeight
      : measuredRowHeight > 0
        ? measuredRowHeight
        : 48;
  const cellHeight = Math.max(
    1,
    (renderedAsDeclaredGrid && !stretchedRenderedRows
      ? renderedRowHeight
      : null)
      ?? fallbackCellHeight,
  );
  const logicalHeight = (rows * cellHeight)
    + (Math.max(0, rows - 1) * rowGap);

  return {
    cellHeight,
    cellWidth,
    columnGap,
    columns,
    // Responsive renderers may stack a declared desktop grid into one or two
    // tracks on mobile. Cover the complete rendered slot with compact,
    // desktop-height interaction rows instead of stretching a few rows over
    // the whole mobile section.
    height: Math.max(rect.height, logicalHeight),
    left: rect.left,
    rowGap,
    rows,
    top: rect.top,
    width: rect.width,
  };
}

export function builderStudioGridFootprint(
  block: SiteBuilderBlock,
  columns: number,
  fallbackFootprint?: Pick<BuilderStudioGridPlacement, "h" | "w">,
): Pick<BuilderStudioGridPlacement, "h" | "w"> {
  const columnCount = positiveInteger(columns, 12);

  return {
    h: positiveInteger(block.options?.gridH, fallbackFootprint?.h ?? 2),
    w: Math.min(
      columnCount,
      positiveInteger(
        block.options?.gridW,
        Math.min(fallbackFootprint?.w ?? 6, columnCount),
      ),
    ),
  };
}

export function builderStudioAutoWrappedColumnContentChanged(
  previous: SiteBuilderBlock | null | undefined,
  next: SiteBuilderBlock,
): boolean {
  if (
    next.type !== "column"
    || next.options?.builderAutoWrapped !== true
  ) {
    return false;
  }

  const signature = (block: SiteBuilderBlock | null | undefined) => (
    block?.slots?.content ?? []
  ).map((child) => [
    child.uuid ?? null,
    child.type,
    child.options?.gridH ?? null,
    child.options?.gridW ?? null,
  ]);

  return JSON.stringify(signature(previous)) !== JSON.stringify(signature(next));
}

export function builderStudioAutoSizeAutoWrappedColumn(
  block: SiteBuilderBlock,
  getFootprint: (
    child: SiteBuilderBlock,
    columns: number,
  ) => Pick<BuilderStudioGridPlacement, "h" | "w"> = (
    child,
    columns,
  ) => builderStudioGridFootprint(child, columns),
): SiteBuilderBlock {
  const rawWidth = Number(block.options?.gridW);
  const rawHeight = Number(block.options?.gridH);

  if (
    block.type !== "column"
    || block.options?.builderAutoWrapped !== true
    || !Number.isFinite(rawWidth)
    || rawWidth < 1
    || !Number.isFinite(rawHeight)
    || rawHeight < 1
  ) {
    return block;
  }

  const width = Math.max(1, Math.round(rawWidth));
  const height = Math.max(1, Math.round(rawHeight));
  const content = block.slots?.content ?? [];
  const baseRows = 2;
  const inlineLayout = (
    block.options?.contentLayout === "horizontal"
    || block.options?.contentLayout === "inline"
    || block.options?.contentLayout === "inlineActions"
  );
  let estimatedRows = baseRows;

  if (content.length > 0 && inlineLayout) {
    let completedRows = 0;
    let currentRowWidth = 0;
    let currentRowHeight = 1;

    content.forEach((child) => {
      const footprint = getFootprint(child, width);
      const childWidth = Math.min(width, Math.max(1, footprint.w));
      const childHeight = Math.max(1, footprint.h);

      if (
        currentRowWidth > 0
        && currentRowWidth + childWidth > width
      ) {
        completedRows += currentRowHeight;
        currentRowWidth = 0;
        currentRowHeight = 1;
      }

      currentRowWidth += childWidth;
      currentRowHeight = Math.max(currentRowHeight, childHeight);
    });

    estimatedRows = Math.max(baseRows, completedRows + currentRowHeight);
  } else if (content.length > 0) {
    estimatedRows = Math.max(
      baseRows,
      content.reduce(
        (rows, child) => rows + Math.max(1, getFootprint(child, width).h),
        0,
      ),
    );
  }

  // Auto-wrapped Columns own their height: it must follow the current
  // contents in both directions. Keeping the previous height as a minimum
  // leaves extended grid rows behind after the inserted child is deleted.
  const nextHeight = estimatedRows;

  return nextHeight !== height
    ? {
        ...block,
        options: {
          ...(block.options ?? {}),
          gridH: nextHeight,
        },
      }
    : block;
}

/**
 * Materializes a cyan grid line for a block that already owns its layout
 * (most notably the text Column wrapper). Such blocks cannot be auto-wrapped
 * into another Column, so the line has to become a real adjacent grid
 * placement. Horizontal lines are offered only when the requested side fits;
 * below can always extend the grid vertically.
 */
export function builderStudioMaterializeGridLineDrop({
  anchor,
  columns,
  dropLine,
  incoming,
}: {
  anchor: SiteBuilderBlock;
  columns: number;
  dropLine: SiteBuilderDropLine;
  incoming: SiteBuilderBlock;
}): BuilderStudioMaterializedGridLineDrop | null {
  if (
    dropLine !== "left"
    && dropLine !== "right"
    && dropLine !== "below"
  ) {
    return null;
  }

  const columnCount = positiveInteger(columns, 12);
  const anchorWidth = Math.min(
    columnCount,
    positiveInteger(anchor.options?.gridW, Math.min(6, columnCount)),
  );
  const anchorX = Math.max(
    1,
    Math.min(
      positiveInteger(anchor.options?.gridX, 1),
      columnCount - anchorWidth + 1,
    ),
  );
  const anchorY = positiveInteger(anchor.options?.gridY, 1);
  const anchorHeight = positiveInteger(anchor.options?.gridH, 1);
  const footprint = builderStudioGridFootprint(incoming, columnCount);
  let placement: BuilderStudioGridPlacement;

  if (dropLine === "left") {
    if (anchorX <= footprint.w) {
      return null;
    }

    placement = {
      ...footprint,
      x: anchorX - footprint.w,
      y: anchorY,
    };
  } else if (dropLine === "right") {
    const x = anchorX + anchorWidth;

    if (x + footprint.w - 1 > columnCount) {
      return null;
    }

    placement = {
      ...footprint,
      x,
      y: anchorY,
    };
  } else {
    placement = {
      ...footprint,
      x: Math.min(anchorX, columnCount - footprint.w + 1),
      y: anchorY + anchorHeight,
    };
  }

  return {
    block: {
      ...incoming,
      options: {
        ...(incoming.options ?? {}),
        gridH: placement.h,
        gridW: placement.w,
        gridX: placement.x,
        gridY: placement.y,
      },
    },
    placement,
  };
}

/**
 * A structured cyan line is different from a free-grid pointer drop: it must
 * never create a new collision. Free-grid placement intentionally remains
 * bounds-only and can still overlap through
 * `builderStudioGridPlacementWithinBounds`.
 */
export function builderStudioMaterializeAvailableGridLineDrop({
  anchor,
  blocks,
  columns,
  dropLine,
  ignoredIndex = null,
  incoming,
}: {
  anchor: SiteBuilderBlock;
  blocks: readonly SiteBuilderBlock[];
  columns: number;
  dropLine: SiteBuilderDropLine;
  ignoredIndex?: number | null;
  incoming: SiteBuilderBlock;
}): BuilderStudioMaterializedGridLineDrop | null {
  const materialized = builderStudioMaterializeGridLineDrop({
    anchor,
    columns,
    dropLine,
    incoming,
  });

  if (
    !materialized
    || !builderStudioGridPlacementAvailable({
      blocks,
      columns,
      ignoredIndex,
      placement: materialized.placement,
    })
  ) {
    return null;
  }

  return materialized;
}

export function builderStudioExtendedGridMetrics({
  clientY,
  footprint,
  maxExtraRows = BUILDER_STUDIO_EXTENDED_GRID_MAX_EXTRA_ROWS,
  metrics,
  minimumRows = metrics.rows,
}: {
  clientY: number;
  footprint: Pick<BuilderStudioGridPlacement, "h" | "w">;
  maxExtraRows?: number;
  metrics: BuilderStudioGridMetrics;
  minimumRows?: number;
}): BuilderStudioGridMetrics {
  if (maxExtraRows <= 0) {
    return metrics;
  }

  const step = metrics.cellHeight + metrics.rowGap;
  const pointerRow = Math.floor((clientY - metrics.top) / step) + 1;
  const maximumRows = metrics.rows + Math.floor(maxExtraRows);
  const rows = Math.max(
    metrics.rows,
    Math.min(maximumRows, minimumRows),
    Math.min(
      maximumRows,
      pointerRow + Math.max(1, footprint.h) - 1,
    ),
  );

  return rows === metrics.rows
    ? metrics
    : {
        ...metrics,
        height: (rows * metrics.cellHeight)
          + (Math.max(0, rows - 1) * metrics.rowGap),
        rows,
      };
}

export function builderStudioGridPlacementForMetrics({
  clientX,
  clientY,
  footprint,
  metrics,
}: {
  clientX: number;
  clientY: number;
  footprint: Pick<BuilderStudioGridPlacement, "h" | "w">;
  metrics: BuilderStudioGridMetrics;
}): BuilderStudioGridPlacement {
  const stepX = metrics.cellWidth + metrics.columnGap;
  const stepY = metrics.cellHeight + metrics.rowGap;
  const w = Math.min(metrics.columns, Math.max(1, footprint.w));
  const h = Math.max(1, footprint.h);
  const pointerColumn = Math.floor((clientX - metrics.left) / stepX) + 1;
  const pointerRow = Math.floor((clientY - metrics.top) / stepY) + 1;

  return {
    h,
    w,
    x: Math.max(
      1,
      Math.min(pointerColumn, metrics.columns - w + 1),
    ),
    y: Math.max(1, Math.min(pointerRow, metrics.rows - h + 1)),
  };
}

export function builderStudioGridPlacementRect(
  metrics: BuilderStudioGridMetrics,
  placement: BuilderStudioGridPlacement,
): AdvancedCanvasRect {
  return {
    height: Math.max(
      metrics.cellHeight,
      (placement.h * metrics.cellHeight)
        + (Math.max(0, placement.h - 1) * metrics.rowGap),
    ),
    left: metrics.left
      + ((placement.x - 1) * (metrics.cellWidth + metrics.columnGap)),
    top: metrics.top
      + ((placement.y - 1) * (metrics.cellHeight + metrics.rowGap)),
    width: Math.max(
      metrics.cellWidth,
      (placement.w * metrics.cellWidth)
        + (Math.max(0, placement.w - 1) * metrics.columnGap),
    ),
  };
}

export function builderStudioGridPlacementAvailable({
  blocks,
  columns,
  ignoredIndex = null,
  placement,
}: BuilderStudioGridAvailabilityInput): boolean {
  if (!builderStudioGridPlacementWithinBounds({ columns, placement })) {
    return false;
  }

  const right = placement.x + placement.w - 1;
  const bottom = placement.y + placement.h - 1;

  return blocks.every((block, index) => {
    if (index === ignoredIndex) {
      return true;
    }

    const options = block.options ?? {};
    const x = positiveInteger(options.gridX, 1);
    const y = positiveInteger(options.gridY, 1);
    const width = positiveInteger(options.gridW, 2);
    const height = positiveInteger(options.gridH, 1);
    const blockRight = x + width - 1;
    const blockBottom = y + height - 1;

    return (
      right < x
      || placement.x > blockRight
      || bottom < y
      || placement.y > blockBottom
    );
  });
}

/**
 * Pointer placement and resizing allow intentional overlap. They only need to
 * stay inside the horizontal grid bounds; rows can grow through extended
 * slots. Collision-aware placement remains available for non-pointer sidebar
 * insertion where there is no user-selected coordinate.
 */
export function builderStudioGridPlacementWithinBounds({
  columns,
  placement,
}: BuilderStudioGridBoundsInput): boolean {
  return placement.x >= 1
    && placement.y >= 1
    && placement.w >= 1
    && placement.h >= 1
    && placement.x + placement.w - 1 <= Math.max(1, columns);
}

/**
 * Finds a deterministic free grid position for non-pointer insertions.
 *
 * Dragging already carries an exact pointer-derived placement. Sidebar clicks
 * do not, so they first try the space to the right of the current anchor,
 * then below it, and finally scan the grid from top to bottom. Extending below
 * the occupied rows guarantees that a valid insertion never overlaps an
 * existing block.
 */
export function builderStudioFirstAvailableGridPlacement({
  anchor = null,
  blocks,
  columns,
  footprint,
}: BuilderStudioAutomaticGridPlacementInput): BuilderStudioGridPlacement {
  const columnCount = positiveInteger(columns, 12);
  const w = Math.min(columnCount, positiveInteger(footprint.w, 1));
  const h = positiveInteger(footprint.h, 1);
  const placement = (
    x: number,
    y: number,
  ): BuilderStudioGridPlacement => ({
    h,
    w,
    x: Math.max(1, Math.min(columnCount - w + 1, x)),
    y: Math.max(1, y),
  });
  const candidates: BuilderStudioGridPlacement[] = [];

  if (anchor) {
    const anchorX = positiveInteger(anchor.options?.gridX, 1);
    const anchorY = positiveInteger(anchor.options?.gridY, 1);
    const anchorW = Math.min(
      columnCount,
      positiveInteger(anchor.options?.gridW, 2),
    );
    const anchorH = positiveInteger(anchor.options?.gridH, 1);

    if (anchorX + anchorW + w - 1 <= columnCount) {
      candidates.push(placement(anchorX + anchorW, anchorY));
    }

    for (let x = 1; x <= columnCount - w + 1; x += 1) {
      candidates.push(placement(x, anchorY));
    }

    candidates.push(placement(anchorX, anchorY + anchorH));
  }

  for (const candidate of candidates) {
    if (builderStudioGridPlacementAvailable({
      blocks,
      columns: columnCount,
      placement: candidate,
    })) {
      return candidate;
    }
  }

  const finalOccupiedRow = blocks.reduce((row, block) => Math.max(
    row,
    positiveInteger(block.options?.gridY, 1)
      + positiveInteger(block.options?.gridH, 1)
      - 1,
  ), 0);

  for (let y = 1; y <= finalOccupiedRow + 1; y += 1) {
    for (let x = 1; x <= columnCount - w + 1; x += 1) {
      const candidate = placement(x, y);

      if (builderStudioGridPlacementAvailable({
        blocks,
        columns: columnCount,
        placement: candidate,
      })) {
        return candidate;
      }
    }
  }

  return placement(1, finalOccupiedRow + 1);
}

/**
 * Maps a visually stacked responsive insertion back to deterministic desktop
 * grid coordinates. The source is excluded before measuring available space,
 * while the visual index still refers to the rendered collection that contains
 * it. Prefer the immediate side of the preceding/following item and then move
 * downward, so a mobile reorder also has a meaningful desktop result.
 */
export function builderStudioResponsiveGridPlacement({
  blocks,
  columns,
  footprint,
  ignoredIndex = null,
  visualInsertIndex,
}: {
  blocks: readonly SiteBuilderBlock[];
  columns: number;
  footprint: Pick<BuilderStudioGridPlacement, "h" | "w">;
  ignoredIndex?: number | null;
  visualInsertIndex: number;
}): BuilderStudioGridPlacement {
  const columnCount = positiveInteger(columns, 12);
  const w = Math.min(columnCount, positiveInteger(footprint.w, 1));
  const h = positiveInteger(footprint.h, 1);
  const remainingBlocks = ignoredIndex === null
    ? [...blocks]
    : blocks.filter((_, index) => index !== ignoredIndex);
  const adjustedInsertIndex = Math.max(
    0,
    Math.min(
      remainingBlocks.length,
      visualInsertIndex - (
        ignoredIndex !== null && ignoredIndex < visualInsertIndex ? 1 : 0
      ),
    ),
  );
  const previous = adjustedInsertIndex > 0
    ? remainingBlocks[adjustedInsertIndex - 1] ?? null
    : null;
  const next = remainingBlocks[adjustedInsertIndex] ?? null;
  const placement = (x: number, y: number): BuilderStudioGridPlacement => ({
    h,
    w,
    x: Math.max(1, Math.min(columnCount - w + 1, Math.round(x))),
    y: Math.max(1, Math.round(y)),
  });
  const available = (candidate: BuilderStudioGridPlacement) => (
    builderStudioGridPlacementAvailable({
      blocks: remainingBlocks,
      columns: columnCount,
      placement: candidate,
    })
  );

  if (previous) {
    const previousX = positiveInteger(previous.options?.gridX, 1);
    const previousY = positiveInteger(previous.options?.gridY, 1);
    const previousWidth = Math.min(
      columnCount,
      positiveInteger(previous.options?.gridW, 1),
    );
    const previousHeight = positiveInteger(previous.options?.gridH, 1);
    const right = placement(previousX + previousWidth, previousY);

    if (
      previousX + previousWidth + w - 1 <= columnCount
      && available(right)
    ) {
      return right;
    }

    const below = placement(previousX, previousY + previousHeight);

    if (available(below)) {
      return below;
    }
  } else if (next) {
    const nextX = positiveInteger(next.options?.gridX, 1);
    const nextY = positiveInteger(next.options?.gridY, 1);
    const left = placement(nextX - w, nextY);

    if (nextX > w && available(left)) {
      return left;
    }

    const above = placement(nextX, nextY - h);

    if (nextY > h && available(above)) {
      return above;
    }
  }

  const firstCandidateRow = previous
    ? positiveInteger(previous.options?.gridY, 1)
      + positiveInteger(previous.options?.gridH, 1)
    : 1;
  const finalOccupiedRow = remainingBlocks.reduce((row, block) => Math.max(
    row,
    positiveInteger(block.options?.gridY, 1)
      + positiveInteger(block.options?.gridH, 1)
      - 1,
  ), 0);

  for (let y = firstCandidateRow; y <= finalOccupiedRow + 1; y += 1) {
    for (let x = 1; x <= columnCount - w + 1; x += 1) {
      const candidate = placement(x, y);

      if (available(candidate)) {
        return candidate;
      }
    }
  }

  return placement(1, finalOccupiedRow + 1);
}

function builderStudioGridPlacementsOverlap(
  left: BuilderStudioGridPlacement,
  right: BuilderStudioGridPlacement,
): boolean {
  return !(
    left.x + left.w - 1 < right.x
    || left.x > right.x + right.w - 1
    || left.y + left.h - 1 < right.y
    || left.y > right.y + right.h - 1
  );
}

/**
 * Reorders the desktop placements that correspond to a mobile flow move.
 *
 * Responsive renderers stack a desktop grid in document order. Updating only
 * the moved block can therefore put it first on mobile but leave it to the
 * right/below its anchor on desktop. Rotate the existing visual positions over
 * the affected document interval when that is collision-safe. Different block
 * footprints may make that rotation overlap; in that case, repack only the
 * affected blocks in visual order while leaving every outside block untouched.
 */
export function builderStudioResponsiveGridReflowPlacements({
  blocks,
  columns,
  ignoredIndex,
  visualInsertIndex,
}: BuilderStudioResponsiveGridReflowInput): BuilderStudioGridPlacement[] | null {
  if (ignoredIndex < 0 || ignoredIndex >= blocks.length) {
    return null;
  }

  const columnCount = positiveInteger(columns, 12);
  const originalPlacements = blocks.map((block): BuilderStudioGridPlacement => {
    const w = Math.min(
      columnCount,
      positiveInteger(block.options?.gridW, 2),
    );

    return {
      h: positiveInteger(block.options?.gridH, 1),
      w,
      x: Math.max(
        1,
        Math.min(
          columnCount - w + 1,
          positiveInteger(block.options?.gridX, 1),
        ),
      ),
      y: positiveInteger(block.options?.gridY, 1),
    };
  });
  const remainingIndexes = blocks
    .map((_, index) => index)
    .filter((index) => index !== ignoredIndex);
  const adjustedInsertIndex = Math.max(
    0,
    Math.min(
      remainingIndexes.length,
      visualInsertIndex - (ignoredIndex < visualInsertIndex ? 1 : 0),
    ),
  );
  const reorderedIndexes = [...remainingIndexes];

  reorderedIndexes.splice(adjustedInsertIndex, 0, ignoredIndex);

  const firstChangedIndex = reorderedIndexes.findIndex(
    (blockIndex, index) => blockIndex !== index,
  );

  if (firstChangedIndex < 0) {
    return originalPlacements;
  }

  let lastChangedIndex = reorderedIndexes.length - 1;

  while (
    lastChangedIndex > firstChangedIndex
    && reorderedIndexes[lastChangedIndex] === lastChangedIndex
  ) {
    lastChangedIndex -= 1;
  }

  const affectedIndexes = reorderedIndexes.slice(
    firstChangedIndex,
    lastChangedIndex + 1,
  );
  const affectedIndexSet = new Set(affectedIndexes);
  const visualOrigins = originalPlacements
    .filter((_, index) => affectedIndexSet.has(index))
    .map(({ x, y }) => ({ x, y }))
    .sort((left, right) => left.y - right.y || left.x - right.x);
  const rotatedPlacements = [...originalPlacements];

  affectedIndexes.forEach((blockIndex, index) => {
    const origin = visualOrigins[index];
    const original = originalPlacements[blockIndex];

    if (origin && original) {
      rotatedPlacements[blockIndex] = {
        ...original,
        x: origin.x,
        y: origin.y,
      };
    }
  });

  const rotationIsSafe = rotatedPlacements.every((placement, index) => (
    builderStudioGridPlacementWithinBounds({
      columns: columnCount,
      placement,
    })
    && rotatedPlacements.every((candidate, candidateIndex) => (
      candidateIndex <= index
      || !builderStudioGridPlacementsOverlap(placement, candidate)
      || builderStudioGridPlacementsOverlap(
        originalPlacements[index]!,
        originalPlacements[candidateIndex]!,
      )
    ))
  ));

  if (rotationIsSafe) {
    return rotatedPlacements;
  }

  const reflowedPlacements = [...originalPlacements];
  const occupied = originalPlacements.filter(
    (_, index) => !affectedIndexSet.has(index),
  );
  const firstOrigin = visualOrigins[0] ?? { x: 1, y: 1 };
  const finalOriginalRow = originalPlacements.reduce(
    (row, placement) => Math.max(row, placement.y + placement.h - 1),
    1,
  );
  const scanEndRow = finalOriginalRow + affectedIndexes.reduce(
    (rows, blockIndex) => rows + (originalPlacements[blockIndex]?.h ?? 1),
    affectedIndexes.length,
  );
  let cursorX = firstOrigin.x;
  let cursorY = firstOrigin.y;

  for (const blockIndex of affectedIndexes) {
    const footprint = originalPlacements[blockIndex];

    if (!footprint) {
      continue;
    }

    let resolved: BuilderStudioGridPlacement | null = null;

    for (let y = cursorY; y <= scanEndRow && !resolved; y += 1) {
      const startX = y === cursorY ? cursorX : 1;

      for (let x = startX; x <= columnCount - footprint.w + 1; x += 1) {
        const candidate = {
          ...footprint,
          x,
          y,
        };

        if (occupied.every(
          (placement) => !builderStudioGridPlacementsOverlap(
            placement,
            candidate,
          ),
        )) {
          resolved = candidate;
          break;
        }
      }
    }

    // The scan bound includes every affected height, so a free row must exist.
    // Keep a defensive append fallback for malformed or oversized documents.
    resolved ??= {
      ...footprint,
      x: 1,
      y: scanEndRow + 1,
    };
    reflowedPlacements[blockIndex] = resolved;
    occupied.push(resolved);

    if (resolved.x + resolved.w <= columnCount) {
      cursorX = resolved.x + resolved.w;
      cursorY = resolved.y;
    } else {
      cursorX = 1;
      cursorY = resolved.y + 1;
    }
  }

  return reflowedPlacements;
}

export function builderStudioGridCellOverlays({
  activePlacement,
  blocked,
  keyPrefix,
  metrics,
  toCanvasRect,
}: {
  activePlacement: BuilderStudioGridPlacement | null;
  blocked: boolean;
  keyPrefix: string;
  metrics: BuilderStudioGridMetrics;
  toCanvasRect: (rect: AdvancedCanvasRect) => AdvancedCanvasRect;
}): AdvancedCanvasSlotOverlay[] {
  const cells = Array.from(
    { length: metrics.columns * metrics.rows },
    (_, index) => {
      const column = index % metrics.columns;
      const row = Math.floor(index / metrics.columns);
      const rect = toCanvasRect({
        height: metrics.cellHeight,
        left: metrics.left
          + (column * (metrics.cellWidth + metrics.columnGap)),
        top: metrics.top
          + (row * (metrics.cellHeight + metrics.rowGap)),
        width: metrics.cellWidth,
      });

      return {
        active: false,
        blocked,
        ...rect,
        key: `${keyPrefix}:grid-cell:${column + 1}:${row + 1}`,
        kind: "grid" as const,
      };
    },
  );

  if (!activePlacement) {
    return cells;
  }

  return [
    ...cells,
    {
      active: true,
      blocked,
      ...toCanvasRect(
        builderStudioGridPlacementRect(metrics, activePlacement),
      ),
      key: [
        keyPrefix,
        "grid-active",
        activePlacement.x,
        activePlacement.y,
        activePlacement.w,
        activePlacement.h,
      ].join(":"),
      kind: "grid",
    },
  ];
}

type VisualRowEntry = BuilderStudioMeasuredChild & {
  rowIndex: number;
};

function visualRows(
  children: readonly BuilderStudioMeasuredChild[],
): VisualRowEntry[][] {
  const rows: VisualRowEntry[][] = [];

  [...children]
    .sort((left, right) => (
      left.rect.top - right.rect.top
      || left.rect.left - right.rect.left
    ))
    .forEach((child) => {
      const rowIndex = rows.findIndex((row) => {
        const minTop = Math.min(...row.map((entry) => entry.rect.top));
        const maxTop = Math.max(...row.map((entry) => entry.rect.top));

        return child.rect.top >= minTop - 12
          && child.rect.top <= maxTop + 12;
      });

      if (rowIndex >= 0) {
        rows[rowIndex]?.push({
          ...child,
          rowIndex,
        });
        rows[rowIndex]?.sort(
          (left, right) => left.rect.left - right.rect.left,
        );
      } else {
        rows.push([{
          ...child,
          rowIndex: rows.length,
        }]);
      }
    });

  return rows;
}

export function builderStudioVisualInsertIndex(
  clientX: number,
  clientY: number,
  children: readonly BuilderStudioMeasuredChild[],
): number {
  const rows = visualRows(children);
  let traversed = 0;

  for (const row of rows) {
    const rowTop = Math.min(...row.map((entry) => entry.rect.top));
    const rowBottom = Math.max(...row.map((entry) => entry.rect.bottom));

    if (clientY <= rowBottom) {
      if (row.length === 1) {
        const entry = row[0];

        return traversed + (
          entry && clientY > entry.rect.top + (entry.rect.height / 2)
            ? 1
            : 0
        );
      }

      if (clientY < rowTop) {
        return traversed;
      }

      const column = row.findIndex(
        (entry) => clientX < entry.rect.left + (entry.rect.width / 2),
      );

      return traversed + (column < 0 ? row.length : column);
    }

    traversed += row.length;
  }

  return children.length;
}

export function builderStudioLineBoundaryOverlays({
  activeIndex,
  children,
  containerRect,
  keyPrefix,
  toCanvasRect,
}: {
  activeIndex: number;
  children: readonly BuilderStudioMeasuredChild[];
  containerRect: AdvancedCanvasRect;
  keyPrefix: string;
  toCanvasRect: (rect: AdvancedCanvasRect) => AdvancedCanvasRect;
}): AdvancedCanvasSlotOverlay[] {
  const rows = visualRows(children);
  const ordered = rows.flat();

  if (ordered.length === 0) {
    return [{
      active: activeIndex === 0,
      blocked: false,
      ...toCanvasRect({
        height: 4,
        left: containerRect.left,
        top: containerRect.top
          + Math.min(12, Math.max(0, containerRect.height / 2))
          - 2,
        width: Math.max(80, containerRect.width),
      }),
      key: `${keyPrefix}:line:0`,
      kind: "row",
    }];
  }

  return Array.from({ length: ordered.length + 1 }, (_, index) => {
    const previous = ordered[index - 1] ?? null;
    const next = ordered[index] ?? null;
    const sameRow = previous !== null
      && next !== null
      && previous.rowIndex === next.rowIndex;
    const boundaryRow = sameRow
      ? rows[previous.rowIndex] ?? []
      : next
        ? rows[next.rowIndex] ?? []
        : previous
          ? rows[previous.rowIndex] ?? []
          : [];
    const rowLeft = boundaryRow.length > 0
      ? Math.min(...boundaryRow.map((entry) => entry.rect.left))
      : containerRect.left;
    const rowRight = boundaryRow.length > 0
      ? Math.max(...boundaryRow.map((entry) => entry.rect.right))
      : containerRect.left + containerRect.width;
    const rowTop = boundaryRow.length > 0
      ? Math.min(...boundaryRow.map((entry) => entry.rect.top))
      : containerRect.top;
    const rowBottom = boundaryRow.length > 0
      ? Math.max(...boundaryRow.map((entry) => entry.rect.bottom))
      : containerRect.top + containerRect.height;
    const columnLine = sameRow
      || (index === 0 && (next ? boundaryRow.length > 1 : false))
      || (
        index === ordered.length
        && (previous ? boundaryRow.length > 1 : false)
      );
    const position = sameRow && previous && next
      ? (previous.rect.right + next.rect.left) / 2
      : next
        ? next.rect.left
        : previous
          ? previous.rect.right
          : rowLeft;
    const top = next?.rect.top ?? previous?.rect.bottom ?? rowTop;
    const rect = toCanvasRect(
      columnLine
        ? {
            height: Math.max(36, rowBottom - rowTop),
            left: position - 2,
            top: rowTop,
            width: 4,
          }
        : {
            height: 4,
            left: rowLeft,
            top: top - 2,
            width: Math.max(80, rowRight - rowLeft),
          },
    );

    return {
      active: activeIndex === index,
      blocked: false,
      ...rect,
      key: `${keyPrefix}:line:${index}`,
      kind: "row",
    };
  });
}

export function builderStudioResizeHandlesForBlock(
  block: SiteBuilderBlock | null,
  _nested = false,
): AdvancedCanvasResizeHandle[] {
  if (
    !block
  ) {
    return [];
  }

  return block.type === "separator"
    ? ["n", "s"]
    : ["n", "e", "s", "w"];
}

export function builderStudioBlockUsesButtonPadding(
  block: SiteBuilderBlock,
): boolean {
  return buttonLikeBlockTypes.has(block.type);
}

export function builderStudioResizeOptionsForBlock({
  block,
  cellHeight,
  cellWidth,
  definition,
  deltaX,
  deltaY,
  handle,
  height,
  parentZoneKind,
  width,
}: {
  block: SiteBuilderBlock;
  cellHeight?: number;
  cellWidth?: number;
  definition: SiteBuilderBlockDefinition | null;
  deltaX: number;
  deltaY: number;
  handle: AdvancedCanvasResizeHandle;
  height: number;
  parentZoneKind: SiteBuilderZoneKind | null;
  width: number;
}): Record<string, unknown> {
  const options = block.options ?? {};
  const horizontal = handle === "e" || handle === "w";
  const vertical = handle === "n" || handle === "s";

  if (
    block.type !== "separator"
    &&
    parentZoneKind === "grid"
    && (horizontal ? cellWidth : cellHeight)
  ) {
    const start: BuilderStudioGridPlacement = {
      h: positiveInteger(options.gridH, 1),
      w: positiveInteger(options.gridW, 2),
      x: positiveInteger(options.gridX, 1),
      y: positiveInteger(options.gridY, 1),
    };
    const next = { ...start };

    if (horizontal && cellWidth) {
      const change = Math.round(deltaX / cellWidth);

      if (handle === "e") {
        next.w = Math.max(1, start.w + change);
      } else {
        const nextX = Math.max(
          1,
          Math.min(start.x + change, start.x + start.w - 1),
        );

        next.w = start.w + start.x - nextX;
        next.x = nextX;
      }
    }

    if (vertical && cellHeight) {
      const change = Math.round(deltaY / cellHeight);

      if (handle === "s") {
        next.h = Math.max(1, start.h + change);
      } else {
        const desiredY = start.y + change;
        const nextY = Math.max(
          1,
          Math.min(desiredY, start.y + start.h - 1),
        );

        next.h = start.h + start.y - nextY;
        next.y = nextY;

        if (desiredY < 1) {
          next.h = Math.max(1, start.h + (1 - desiredY));
          next.y = 1;
        }
      }
    }

    return {
      gridH: next.h,
      gridW: next.w,
      gridX: next.x,
      gridY: next.y,
    };
  }

  if (horizontal && builderStudioBlockUsesButtonPadding(block)) {
    const key = handle === "w" ? "paddingLeft" : "paddingRight";
    const direction = handle === "w" ? -deltaX : deltaX;

    return {
      [key]: `${Math.round(
        Math.max(
          0,
          Math.min(360, cssPixelValue(options[key], 0) + direction),
        ),
      )}px`,
    };
  }

  if (horizontal) {
    const nextWidth = handle === "w" ? width - deltaX : width + deltaX;

    return {
      width: `${Math.round(
        Math.max(48, Math.min(2400, nextWidth)),
      )}px`,
    };
  }

  if (vertical && definition?.layoutContainer) {
    const nextHeight = handle === "n" ? height - deltaY : height + deltaY;

    return {
      minHeight: `${Math.round(
        Math.max(24, Math.min(2400, nextHeight)),
      )}px`,
    };
  }

  if (vertical) {
    const key = handle === "n" ? "paddingTop" : "paddingBottom";
    const direction = handle === "n" ? -deltaY : deltaY;

    return {
      [key]: `${Math.round(
        Math.max(
          0,
          Math.min(360, cssPixelValue(options[key], 0) + direction),
        ),
      )}px`,
    };
  }

  return {};
}

export function builderStudioDragAutoScrollSpeed({
  clientY,
  rect,
}: {
  clientY: number;
  rect: Pick<DOMRect, "bottom" | "height" | "top">;
}): number {
  const edge = Math.min(120, Math.max(48, rect.height * 0.12));
  const topDistance = clientY - rect.top;
  const bottomDistance = rect.bottom - clientY;

  if (topDistance < edge) {
    return -Math.max(
      2,
      Math.round(((edge - Math.max(0, topDistance)) / edge) * 24),
    );
  }

  if (bottomDistance < edge) {
    return Math.max(
      2,
      Math.round(((edge - Math.max(0, bottomDistance)) / edge) * 24),
    );
  }

  return 0;
}

export function builderStudioDragScrollElement(
  canvas: HTMLElement,
  ownerDocument: Document,
): HTMLElement {
  const frameElement = ownerDocument.defaultView?.frameElement;
  const scrollingElement = ownerDocument.scrollingElement;

  return frameElement && scrollingElement
    ? scrollingElement as HTMLElement
    : canvas;
}

/**
 * Mirrors the original Shop resize interaction without leaking global body
 * styles after mouseup, window blur, edit-mode exit, or component unmount.
 */
export function lockBuilderStudioBodyResize(
  body: Pick<HTMLElement, "style">,
  cursor: string,
): () => void {
  const previousCursor = body.style.cursor;
  const previousUserSelect = body.style.userSelect;
  let released = false;

  body.style.cursor = cursor;
  body.style.userSelect = "none";

  return () => {
    if (released) {
      return;
    }

    released = true;
    body.style.cursor = previousCursor;
    body.style.userSelect = previousUserSelect;
  };
}
